import { isEmpty } from '@jjj/components';
import { keyBy } from 'src/helpers/core';
import { IField } from '@dash/editForm';
import actionsTypes from './actionTypes';
import {
  Attribute,
  IAddCreditCardState,
  IChangePropertyExtendedPayload,
  IFieldTypes,
  ISetupIntentCardSuccessPayload,
} from './types';
import { createReducer, getError } from '../../../../../../../../../helpers/reducer';
import { t } from '../../../../../../../../../i18n';

function validate(attr: Attribute, value: IFieldTypes, error: string = '') {
  let errorMessage = error;

  if (attr === 'number') {
    // @ts-ignore
    if (value !== undefined && !global.Stripe?.card?.validateCardNumber(value.split('_')[0])) {
      errorMessage = t('dash.validation.valid_error', {
        fieldName: t('dash.units.unit.cards.card_number_title'),
      });
    }
  }

  if (attr === 'name') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.cards.name_on_card_title'),
      });
    }
  }

  if (attr === 'expiry_month') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.select_an_option');
    }
  }

  if (attr === 'expiry_year') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.select_an_option');
    }
  }

  if (attr === 'cvv' && typeof value === 'string') {
    const intValue = parseInt(value, 10);

    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.cards.cvv_code_title'),
      });
    } else if (value.length > 4 || value.length < 3 || intValue.toString() !== value) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.cards.valid_digit_code'),
      });
    }
  }

  return errorMessage;
}

const getInitialField = (fieldName: Attribute, value = null) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || ''),
});

const initialEditForm = {
  name: getInitialField('name'),
  number: getInitialField('number'),
  expiry_month: getInitialField('expiry_month'),
  expiry_year: getInitialField('expiry_year'),
  exp_month: getInitialField('exp_month'),
  exp_year: getInitialField('exp_year'),
  cvv: getInitialField('cvv'),
};

const initState: IAddCreditCardState = {
  isLoading: false,
  editForm: initialEditForm,
  isModalVisible: false,
  clientSecret: '',
};

function onChangeProperty(
  state: IAddCreditCardState,
  { payload }: { payload: IChangePropertyExtendedPayload },
): IAddCreditCardState {
  const { attr, value, error } = payload;
  const { editForm } = state;

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val, error),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onAddCreditCardRequest(state: IAddCreditCardState): IAddCreditCardState {
  return {
    ...state,
    isLoading: true,
  };
}

function onAddCreditCardSuccess(state: IAddCreditCardState): IAddCreditCardState {
  return {
    ...state,
    isLoading: false,
    editForm: initialEditForm,
  };
}

function onAddCreditCardError(
  state: IAddCreditCardState,
  { payload }: { payload: { [key: string]: string } },
): IAddCreditCardState {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    (entry: [Attribute, IField]) => {
      return entry[0];
    },
    (entry: [Attribute, IField]) => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    isLoading: false,
    editForm,
  };
}

function clearCvv(state: IAddCreditCardState) {
  const { editForm } = state;

  return {
    ...state,
    editForm: {
      ...editForm,
      cvv: getInitialField('cvv'),
    },
  };
}

function onProcessActionRequest(state: IAddCreditCardState) {
  return {
    ...state,
    isLoading: true,
  };
}

function onChangeModalVisibility(state: IAddCreditCardState) {
  return {
    ...state,
    isModalVisible: !state.isModalVisible,
  };
}

function onSetupIntentCard(state: IAddCreditCardState, { payload }: { payload: ISetupIntentCardSuccessPayload }) {
  const { clientSecret } = payload;

  return {
    ...state,
    clientSecret,
  };
}

export default createReducer(initState, {
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.ADD_CREDIT_CARD_REQUEST]: onAddCreditCardRequest,
  [actionsTypes.ADD_CREDIT_CARD_SUCCESS]: onAddCreditCardSuccess,
  [actionsTypes.ADD_CREDIT_CARD_ERROR]: onAddCreditCardError,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.CHANGE_MODAL_VISIBILITY]: onChangeModalVisibility,
  [actionsTypes.CLEAR_CVV]: clearCvv,
  [actionsTypes.SETUP_INTENT_CARD_SUCCESS]: onSetupIntentCard,
});
